'use-strict';

import { handleTransition } from 'Util/transitions.js';
import { CONST, documentReady, assignStyling } from 'Util/util';
import * as debounce from 'lodash/debounce';

class Sidebar {
    constructor() {
        this.sidebar = document.querySelector('.js-configurator-sidebar');
        if (!this.sidebar) return;
        this.sidebarScrollContainer = this.sidebar.closest('.js-configurator-sidebar-scroll-container');
        this.sidebarContentHideOnCollapse = this.sidebar.querySelector('.js-sidebar-hide-on-collapse');

        this.sidebarOffscreen = this.sidebarScrollContainer.classList.contains('offscreen');

        this.sidebarTopOffset = 16; // The space between the sticky header and sticky sidebar
        this.sidebarBottomOffset = 48; // Adjust to your preference

        this.stickyBreakpoint = parseInt(CONST.breakpoints.lg, 10); // The breakpoint at which to become sticky

        this.siteHeader = document.querySelector('header');
        this.headerHeight = parseInt(this.siteHeader.offsetHeight, 10);
        this.configuratorType = document.querySelector('[data-configurator-type]')?.dataset?.configuratorType;

        this.isSidebarOverflowing = false;
        this.sidebarVersion = this.sidebar.getAttribute('data-sidebar-version');
        this.isMobileAccordionsEnabled = this.sidebarVersion === 'v2';

        this.toggleAllAccordionsButton = this.sidebar.querySelector('.js-sidebar-toggle-all-accordions-button');
        if (this.toggleAllAccordionsButton) {
            this.collapsedText = this.toggleAllAccordionsButton.getAttribute('data-text-collapsed');
            this.expandedText = this.toggleAllAccordionsButton.getAttribute('data-text-expanded');
        }

        this.assignMobileVariables();
        this.initAccordions();

        this.reInitializeSidebar = this.reInitializeSidebar.bind(this);
        this.handleFloatingSidebar = this.handleFloatingSidebar.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.isSidebarAtBottom = this.isSidebarAtBottom.bind(this);
        this.isSidebarAtBottomContainer = this.isSidebarAtBottomContainer.bind(this);
        this.isSidebarInContainer = this.isSidebarInContainer.bind(this);
        this.replaceSidebarHtml = this.replaceSidebarHtml.bind(this);
        this.isStep1InViewport = this.isStep1InViewport.bind(this);
        this.openMobileSidebar = this.openMobileSidebar.bind(this);
        this.closeMobileSidebar = this.closeMobileSidebar.bind(this);
        this.calculateMaxHeightMobileSidebarBody = this.calculateMaxHeightMobileSidebarBody.bind(this);
        this.mobileSidebarScrollHandler = this.mobileSidebarScrollHandler.bind(this);
        this.mobileSidebarSlideIn = this.mobileSidebarSlideIn.bind(this);
        this.mobileSidebarSlideOut = this.mobileSidebarSlideOut.bind(this);
        this.assignAccordionsEvents = this.assignAccordionsEvents.bind(this);
        this.initAccordions = this.initAccordions.bind(this);
        this.initializeAllAccordionsToggle = this.initializeAllAccordionsToggle.bind(this);
        this.handleToggleAllAccordions = this.handleToggleAllAccordions.bind(this);
        this.handleToggleAccordion = this.handleToggleAccordion.bind(this);

        this.initialize();
    }

    /**
     * @function initialize
     * @private
     * @description initializes the file
     */
    initialize() {
        documentReady(this.handleFloatingSidebar);

        window.addEventListener(
            'resize',
            debounce(() => {
                this.reInitializeSidebar();
            }),
            150
        );

        window.addEventListener('scroll', this.handleScroll);

        this.assignAccordionsEvents();

        this.sidebar.addEventListener('variationChange', this.replaceSidebarHtml);
        this.sidebar.addEventListener('sidebar:reinitializesidebar', this.reInitializeSidebar);

        this.assignMobileEvents();
    }

    /**
     * @function assignMobileVariables
     * @private
     * @description initializes variables for mobile sidebar
     */
    assignMobileVariables() {
        this.sidebarHeader = document.querySelector('.js-configurator-sidebar-header');
        this.sidebarBody = document.querySelector('.js-configurator-sidebar-body');
        this.sidebarFooter = document.querySelector('.js-configurator-sidebar-footer');
        this.sidebarSubmitBtn = document.querySelector('.js-configurator-submit-btn');
        this.productForm = document.querySelector('#product-form');
        this.tapAreaFooter = document.querySelector('.js-card-footer-tap-area');
        this.tapAreaHeader = document.querySelector('.js-card-header-tap-area');
        this.pageHeader = document.querySelector('.js-page-header');
        this.chatButton = document.querySelector('.dimelo_chat_item_markup');
    }

    /**
     * @function assignMobileEvents
     * @private
     * @description initializes event listeners for mobile sidebar
     */
    assignMobileEvents() {
        this.tapAreaFooter.addEventListener('click', this.openMobileSidebar);
        this.tapAreaHeader.addEventListener('click', this.closeMobileSidebar);
    }

    /**
     * @function notifyQuantityCard
     * @description Notifies the quantity card that shared data info has changed and should be updated.
     * @param {HTMLTemplateElement} fragment - The fragment received from the variationChange event.
     */
    static notifyQuantityCard(fragment) {
        // Grab the shared data container which is part of the current configuration so in vz-group-summary
        const sharedDataContainer = fragment.content.querySelector('vz-group-summary .js-shared-data-container');
        const quantitySelector = document.querySelector('.js-business-mobile-quantity-card vz-quantity-selector');

        if (quantitySelector && sharedDataContainer) {
            quantitySelector.dispatchEvent(
                new CustomEvent('updateSharedData', {
                    detail: {
                        sharedDataContainer
                    }
                })
            );
        }
    }

    /**
     * @function replaceSidebarHtml
     * @param { event } event - The custom event
     * @description Replaces the sidebar with new html from an ajax call, and notifies the quantity card so it can add and update the shared data info.
     */
    replaceSidebarHtml(event) {
        const {
            detail: { fragment }
        } = event;

        // Notify the product card so the shared data container can be added to it
        Sidebar.notifyQuantityCard(fragment);

        // Replace the sidebar summary html with the new html
        this.sidebar.querySelector('.configurator-summary').innerHTML = '';
        // Add new HTML AFTER removal to ensure order of connectedCallback & disconnectedCallback
        this.sidebar.querySelector('.configurator-summary').innerHTML = fragment.content.querySelector('.js-configurator-sidebar .configurator-summary').innerHTML;

        // Update the share button
        const shareButton = this.sidebar.querySelector('vz-copy-to-clipboard');
        if (shareButton) shareButton.replaceWith(fragment.content.querySelector('vz-copy-to-clipboard'));

        // Update variables and event listeners after replacing HTML for mobile sidebar
        this.assignMobileVariables();
        this.initAccordions();
        this.handleScroll();

        if (window.innerWidth < this.stickyBreakpoint) this.assignMobileEvents();

        this.assignAccordionsEvents();
        this.handleFloatingSidebar();
    }

    /**
     * @function handleFloatingSidebar
     * @private
     * @description Handles the styling required for the sidebar to become sticky at certain resolutions
     */
    handleFloatingSidebar() {
        // Show bottom fixed sidebar (mobile)
        if (window.innerWidth < this.stickyBreakpoint) {
            this.mobileSidebarScrollHandler();

            this.sidebar.style.removeProperty('position');
            this.sidebar.style.removeProperty('top');
            this.sidebar.style.removeProperty('bottom');
            this.sidebar.style.removeProperty('width');

            if (this.isStep1InViewport()) {
                this.sidebarScrollContainer.classList.add('visible-on-mobile');
            } else {
                this.sidebarScrollContainer.classList.remove('visible-on-mobile');
            }

            if (this.sidebar.classList.contains('open')) {
                this.openMobileSidebar();
            } else {
                this.closeMobileSidebar();
            }
        } else {
            this.sidebarScrollContainer.classList.remove('visible-on-mobile');

            this.openMobileSidebar();

            this.sidebarBody.style.removeProperty('max-height');

            // Adjust and display chat button for desktop
            if (this.chatButton) this.chatButton.classList.remove('adjust-for-sidebar', 'd-none');

            if (this.isSidebarOverflowing) {
                if (this.isSidebarAtBottom() && this.isSidebarInContainer()) {
                    if (this.isSidebarAtBottomContainer()) {
                        // Sidebar is larger than screen height + the sidebar bottom is at the bottom of the screen
                        // Bottom of sidebar is aligned with configurator -> position absolute
                        assignStyling(this.sidebar, {
                            position: 'absolute',
                            bottom: '30px',
                            width: `${this.sidebarScrollContainer.clientWidth - 24}px`,
                            top: 'auto'
                        });
                    } else {
                        // As long as we can scroll down and the sidebar bottom is not aligned with configurator bottom -> position fixed
                        assignStyling(this.sidebar, {
                            position: 'fixed',
                            bottom: `${this.sidebarBottomOffset}px`,
                            width: `${this.sidebarScrollContainer.clientWidth - 24}px`,
                            top: 'auto'
                        });
                    }
                } else {
                    this.sidebar.style.removeProperty('position');
                }
            } else {
                // Sidebar is smaller than screen height or at the top -> position sticky
                assignStyling(this.sidebar, {
                    position: 'sticky',
                    top: `${this.headerHeight + this.sidebarTopOffset}px`,
                    bottom: 'auto',
                    width: 'initial'
                });
            }
        }
    }

    mobileSidebarSlideOut() {
        this.sidebarScrollContainer.classList.add('offscreen');

        if (this.chatButton) this.chatButton.classList.remove('adjust-for-sidebar');

        this.sidebarOffscreen = true;
    }

    mobileSidebarSlideIn() {
        this.sidebarScrollContainer.classList.remove('offscreen');

        if (this.chatButton) this.chatButton.classList.add('adjust-for-sidebar');

        this.sidebarOffscreen = false;
    }

    mobileSidebarScrollHandler() {
        // Get latest height for accuracy
        this.sidebarScrollContainer = this.sidebar.closest('.js-configurator-sidebar-scroll-container');

        if (window.scrollY >= this.sidebarScrollContainer.offsetHeight || this.sidebar.classList.contains('open')) {
            this.mobileSidebarSlideIn();
        } else {
            this.mobileSidebarSlideOut();
        }
    }

    isSidebarAtBottom() {
        return window.innerHeight - this.sidebar.getBoundingClientRect().bottom - this.sidebarBottomOffset + 1 > 0;
    }

    isSidebarAtBottomContainer() {
        return window.innerHeight - this.sidebarScrollContainer.getBoundingClientRect().bottom - this.sidebarBottomOffset + 30 > 0;
    }

    isSidebarInContainer() {
        return this.sidebar.getBoundingClientRect().top - this.sidebarScrollContainer.getBoundingClientRect().top >= 0;
    }

    handleScroll() {
        this.isSidebarOverflowing = this.sidebar.offsetHeight >= window.innerHeight - this.headerHeight - this.sidebarTopOffset - this.sidebarBottomOffset;

        this.handleFloatingSidebar();
    }

    isStep1InViewport() {
        return window.innerHeight > this.productForm.getBoundingClientRect().top + 2 * this.sidebarBottomOffset;
    }

    openMobileSidebar() {
        this.sidebarHeader.classList.remove('d-none');
        this.sidebarBody.classList.remove('d-none');
        this.sidebar.classList.remove('closed');
        this.sidebar.classList.add('open');
        this.sidebarFooter.classList.add('py-2');

        if (this.configuratorType !== 'business-mobile' && this.configuratorType !== 'teams-calling') this.sidebarFooter.classList.add('mt-2');

        this.sidebarFooter.classList.remove('py-1');

        if (this.sidebarSubmitBtn) {
            this.sidebarSubmitBtn.classList.add('mt-3');
            this.sidebarSubmitBtn.classList.remove('mt-1');
        }

        if (window.innerWidth < this.stickyBreakpoint) {
            assignStyling(this.sidebarBody, {
                'max-height': `${this.calculateMaxHeightMobileSidebarBody()}px`
            });
        }

        if (this.chatButton) this.chatButton.classList.add('d-none');
    }

    closeMobileSidebar() {
        this.sidebarHeader.classList.add('d-none');
        this.sidebarBody.classList.add('d-none');
        this.sidebar.classList.add('closed');
        this.sidebar.classList.remove('open');
        this.sidebarFooter.classList.remove('mt-2', 'py-2');
        this.sidebarFooter.classList.add('py-1');

        if (this.sidebarSubmitBtn) {
            this.sidebarSubmitBtn.classList.remove('mt-3');
            this.sidebarSubmitBtn.classList.add('mt-1');
        }

        if (this.chatButton) this.chatButton.classList.remove('d-none');

        // Only slide out on close when at the top of the page
        if (window.scrollY === 0) this.mobileSidebarSlideOut();
    }

    calculateMaxHeightMobileSidebarBody() {
        return (
            window.innerHeight -
            this.pageHeader.clientHeight -
            this.sidebarFooter.clientHeight -
            this.sidebarHeader.clientHeight -
            0.5 * this.sidebarTopOffset -
            this.sidebarBottomOffset
        );
    }

    /**
     * @function assignAccordionsEvents
     * @description
     * This method assigns event listeners to each accordion element on the sidebar.
     * When an accordion is collapsed (hidden), the 'handleScroll' method is invoked to recalculate sticky sidebar position.
     */
    assignAccordionsEvents() {
        Array.from(document.querySelectorAll('.js-sidebar-accordion-content')).forEach((accordion) => accordion.addEventListener('hidden.bs.collapse', this.handleScroll));

        if (this.toggleAllAccordionsButton) this.initializeAllAccordionsToggle();
    }

    reInitializeSidebar() {
        this.assignMobileVariables();
        this.initAccordions();

        if (window.innerWidth < this.stickyBreakpoint) {
            this.assignMobileEvents();
            this.closeMobileSidebar();
        } else {
            this.sidebarHeader.classList.remove('d-none');
            this.sidebarBody.classList.remove('d-none');

            this.openMobileSidebar();
        }

        this.handleFloatingSidebar();

        if (this.toggleAllAccordionsButton) this.initializeAllAccordionsToggle();
    }

    initializeAllAccordionsToggle() {
        const toggleAllAccordionsButton = this.sidebar.querySelector('.js-sidebar-toggle-all-accordions-button');
        if (toggleAllAccordionsButton) toggleAllAccordionsButton.addEventListener('click', this.handleToggleAllAccordions);

        const accordionHeaders = Array.from(this.sidebar.querySelectorAll('.js-sidebar-accordion-header'));
        accordionHeaders.forEach((header) => {
            const targetContentId = header.getAttribute('data-target-content-id');
            const targetButton = header.querySelector(`button[data-bs-target="${targetContentId}"]`);

            if (targetContentId && targetButton) targetButton.addEventListener('click', this.handleToggleAccordion);
        });

        // Toggle visibility on toggle-all button
        if (accordionHeaders.length > 0) {
            toggleAllAccordionsButton.classList.add('visible');

            // Expand all relevant accordions by default
            this.handleToggleAllAccordions();
        } else {
            toggleAllAccordionsButton.classList.remove('visible');
        }
    }

    findExpandedAccordions() {
        const accordionHeaders = Array.from(this.sidebar.querySelectorAll('.js-sidebar-accordion-header'));
        return accordionHeaders.filter((header) => document.querySelector(header.getAttribute('data-target-content-id')).classList.contains('show'));
    }

    findCollapsedAccordions() {
        const accordionHeaders = Array.from(this.sidebar.querySelectorAll('.js-sidebar-accordion-header'));
        return accordionHeaders.filter((header) => !document.querySelector(header.getAttribute('data-target-content-id')).classList.contains('show'));
    }

    handleToggleAccordion() {
        const toggleAllAccordionsButton = this.sidebar.querySelector('.js-sidebar-toggle-all-accordions-button');
        const sidebarContentHideOnCollapse = this.sidebar.querySelector('.js-sidebar-hide-on-collapse');

        setTimeout(() => {
            const expandedAccordions = this.findExpandedAccordions();
            const collapsedAccordions = this.findCollapsedAccordions();

            // Update toggle-all button if applicable
            if (expandedAccordions.length === 0) {
                toggleAllAccordionsButton.setAttribute('aria-expanded', false);
                toggleAllAccordionsButton.querySelector('.js-sidebar-toggle-all-accordions-text').innerText = this.collapsedText;
            } else if (collapsedAccordions.length === 0) {
                toggleAllAccordionsButton.setAttribute('aria-expanded', true);
                toggleAllAccordionsButton.querySelector('.js-sidebar-toggle-all-accordions-text').innerText = this.expandedText;
            }
        }, 500); // Wait for CSS transitions

        handleTransition(() => {
            const expandedAccordions = this.findExpandedAccordions();

            // Toggle relevant content blocks
            sidebarContentHideOnCollapse.classList.toggle('d-none', expandedAccordions.length === 0);
        });
    }

    /**
     * @function handleToggleAllAccordions
     * @description
     * Toggles all sidebar accordions on click, if there is content to match the triggers.
     */
    handleToggleAllAccordions() {
        const toggleAllAccordionsButton = this.sidebar.querySelector('.js-sidebar-toggle-all-accordions-button');
        const expanded = toggleAllAccordionsButton.getAttribute('aria-expanded');
        const accordionHeaders = Array.from(this.sidebar.querySelectorAll('.js-sidebar-accordion-header'));
        const sidebarContentHideOnCollapse = this.sidebar.querySelector('.js-sidebar-hide-on-collapse');

        if (accordionHeaders) {
            let toggleState = expanded === 'true';

            accordionHeaders.forEach((header) => {
                const targetContentId = header.getAttribute('data-target-content-id');
                const targetButton = header.querySelector(`button[data-bs-target="${targetContentId}"]`);

                // No matching content to toggle
                if (!targetContentId) return;

                const content = document.querySelector(targetContentId);

                // If accordion state is already the target state no need to toggle
                const targetState = expanded !== 'true';
                const currentState = content.classList.contains('show');
                if (targetState === currentState) return;

                toggleState = expanded !== 'true';

                // Toggle accordion to the target state
                targetButton.dispatchEvent(new CustomEvent('click'));
            });

            // Update toggle-all button
            toggleAllAccordionsButton.setAttribute('aria-expanded', toggleState);
            toggleAllAccordionsButton.querySelector('.js-sidebar-toggle-all-accordions-text').innerText = toggleState ? this.expandedText : this.collapsedText;

            handleTransition(() => {
                // Toggle relevant content blocks
                sidebarContentHideOnCollapse.classList.toggle('d-none', !toggleState);
            });
        }
    }

    /**
     * @function initAccordions
     * @description
     * Initializes the accordion functionality based on window width.
     */
    initAccordions() {
        if (this.configuratorType !== 'business-mobile' && this.configuratorType !== 'teams-calling') return;

        this.summaryGroup = document.querySelector('vz-group-summary');
        this.accordions = document.querySelectorAll('.js-sidebar-accordion-content');

        if (window.innerWidth < this.stickyBreakpoint && !this.isMobileAccordionsEnabled) {
            this.summaryGroup?.classList.remove('accordion');

            if (this.accordions.length) {
                Array.from(this.accordions).forEach((element) => element.classList.remove('collapse', 'collapsed'));
            }
        } else {
            this.summaryGroup?.classList.add('accordion');

            if (this.accordions.length) {
                Array.from(this.accordions).forEach((element) => element.classList.add('collapse', 'collapsed'));
            }
        }
    }
}

(() => new Sidebar())();
